import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Building, Shield, Star, CheckCircle, Package, TrendingUp, ChevronLeft, ChevronRight, MapPin, Lock } from 'lucide-react';
import { suppliersApi, countriesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { isPaidUser } from '../utils/membershipUtils';

const SuppliersPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [countries, setCountries] = useState([]);

  const page = parseInt(searchParams.get('page') || '1');
  const country = searchParams.get('country') || '';
  const membership = searchParams.get('membership') || '';
  const verified = searchParams.get('verified') || '';

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = { page, limit: 12 };
        if (country) params.country = country;
        if (membership) params.membership = membership;
        if (verified) params.verified = verified === 'true';

        const [suppliersRes, countriesRes] = await Promise.all([
          suppliersApi.getAll(params),
          countriesApi.getAll()
        ]);
        setSuppliers(suppliersRes.data.suppliers || []);
        setTotalPages(suppliersRes.data.pages || 1);
        setTotal(suppliersRes.data.total || 0);
        setCountries(countriesRes.data || []);
      } catch (error) {
        console.error('Error fetching suppliers:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [page, country, membership, verified]);

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />

      {/* Hero */}
      <section className="bg-[#0A192F] py-12">
        <div className="container-main">
          <h1 className="text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight">Find Suppliers</h1>
          <p className="text-slate-300 max-w-2xl">
            Connect with verified manufacturers and exporters from around the world
          </p>
        </div>
      </section>

      <div className="container-main py-8">
        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-8">
          <div className="flex flex-wrap items-center gap-4">
            <Select value={country || 'all'} onValueChange={(val) => updateFilter('country', val === 'all' ? '' : val)}>
              <SelectTrigger className="w-[180px]" data-testid="suppliers-country-filter">
                <SelectValue placeholder="All Countries" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {countries.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={membership || 'all'} onValueChange={(val) => updateFilter('membership', val === 'all' ? '' : val)}>
              <SelectTrigger className="w-[180px]" data-testid="suppliers-membership-filter">
                <SelectValue placeholder="All Memberships" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Memberships</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="silver">Silver</SelectItem>
                <SelectItem value="gold">Gold</SelectItem>
                <SelectItem value="vip">VIP</SelectItem>
              </SelectContent>
            </Select>

            <Select value={verified || 'all'} onValueChange={(val) => updateFilter('verified', val === 'all' ? '' : val)}>
              <SelectTrigger className="w-[180px]" data-testid="suppliers-verified-filter">
                <SelectValue placeholder="Verification" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Suppliers</SelectItem>
                <SelectItem value="true">Verified Only</SelectItem>
              </SelectContent>
            </Select>

            {(country || membership || verified) && (
              <Button 
                variant="ghost" 
                onClick={() => setSearchParams({})}
                data-testid="suppliers-clear-filters"
              >
                Clear Filters
              </Button>
            )}

            <span className="ml-auto text-sm text-slate-600">
              {total} suppliers found
            </span>
          </div>
        </div>

        {/* Suppliers Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white border rounded-xl p-6 animate-pulse">
                <div className="w-16 h-16 bg-slate-200 rounded-full mx-auto mb-4"></div>
                <div className="h-4 bg-slate-200 rounded w-3/4 mx-auto mb-2"></div>
                <div className="h-3 bg-slate-200 rounded w-1/2 mx-auto"></div>
              </div>
            ))}
          </div>
        ) : suppliers.length === 0 ? (
          <div className="text-center py-16">
            <Building className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No suppliers found</h3>
            <p className="text-slate-600">Try adjusting your filters</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {suppliers.map((supplier) => {
                const isVerifiedLocked = supplier.is_verified && !isPaidUser(user);
                return (
                <div key={supplier.user_id} className="relative">
                  {/* Blur overlay for verified suppliers when user is not paid */}
                  {isVerifiedLocked && (
                    <div className="absolute inset-0 z-10 backdrop-blur-sm bg-white/50 rounded-xl flex flex-col items-center justify-center p-4 text-center" data-testid={`supplier-blur-${supplier.user_id}`}>
                      <Lock className="w-6 h-6 text-[#0A192F] mb-2" />
                      <p className="text-sm font-semibold text-[#0A192F] mb-1">Verified Supplier</p>
                      <p className="text-xs text-slate-500 mb-3">Upgrade to view profile</p>
                      <Button
                        onClick={() => navigate('/membership')}
                        size="sm"
                        className="bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-sm h-8 px-4 text-xs font-semibold"
                        data-testid={`supplier-upgrade-${supplier.user_id}`}
                      >
                        <Shield className="w-3 h-3 mr-1" /> Upgrade
                      </Button>
                    </div>
                  )}
                  <Link
                    to={isVerifiedLocked ? '#' : `/suppliers/${supplier.user_id}`}
                    onClick={(e) => isVerifiedLocked && e.preventDefault()}
                    className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 group block"
                    data-testid={`supplier-item-${supplier.user_id}`}
                  >
                  <div className="text-center mb-4">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#2563EB] to-[#EA580C] flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold group-hover:scale-105 transition-transform">
                      {supplier.company_name?.charAt(0) || 'S'}
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-[#2563EB] transition-colors">
                      {supplier.company_name}
                    </h3>
                    <p className="text-sm text-slate-500 flex items-center justify-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {supplier.country || 'International'}
                    </p>
                  </div>

                  <div className="flex items-center justify-center gap-2 mb-4">
                    {supplier.is_verified && (
                      <Badge className="badge-verified">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Verified
                      </Badge>
                    )}
                    {supplier.membership && supplier.membership !== 'free' && (
                      <Badge className="badge-gold">
                        <Star className="w-3 h-3 mr-1" />
                        {supplier.membership}
                      </Badge>
                    )}
                  </div>

                  {supplier.main_products?.length > 0 && (
                    <p className="text-xs text-slate-500 text-center mb-4 line-clamp-2">
                      {supplier.main_products.slice(0, 3).join(' • ')}
                    </p>
                  )}

                  <div className="grid grid-cols-3 gap-2 pt-4 border-t border-slate-100 text-center text-xs">
                    <div>
                      <p className="font-semibold text-slate-900">{supplier.stats?.products || 0}</p>
                      <p className="text-slate-500">Products</p>
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{supplier.stats?.response_rate || 0}%</p>
                      <p className="text-slate-500">Response</p>
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{supplier.stats?.views || 0}</p>
                      <p className="text-slate-500">Views</p>
                    </div>
                  </div>
                </Link>
                </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-8">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => updateFilter('page', String(page - 1))}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-600">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === totalPages}
                  onClick={() => updateFilter('page', String(page + 1))}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default SuppliersPage;
